import { type SensorData, sensorDataSchema } from '@si/types';
import { get, objectToQueryParams } from '@si/utils';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { queryKeys } from './queryKeys';

const responseSchema = z.object({
  sensorData: z.array(sensorDataSchema),
});

interface Params {
  projectId: string;
  from: string;
  to: string;
  sensorIds?: string; // comma-separated
  enabled?: boolean;
}

export function useSensorData({ projectId, from, to, sensorIds, enabled = true }: Params) {
  const queryKey = queryKeys.getSensorData(projectId, from, to, sensorIds);
  const url = `/projects/${projectId}/sensor-data${objectToQueryParams({
    from,
    to,
    sensorIds,
  })}`;

  const { data, error, isFetching, isLoading } = useQuery<{ sensorData: SensorData[] }>({
    queryKey,
    throwOnError: false,
    queryFn: () => get({ url, schema: responseSchema }),
    enabled: enabled && !!projectId && !!from && !!to,
    staleTime: 5 * 60 * 1000,
  });

  return {
    sensorData: data?.sensorData || [],
    error,
    isFetching,
    isLoading,
  };
}
